import { useMutation, useSuspenseQuery } from '@apollo/client/react';
import { ReactElement, Suspense, useState } from 'react';
import {
  GQL_MUTATION_UPDATE_SOURCE,
  GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
  GQL_QUERY_READ_INTEGRATION,
  GQL_QUERY_READ_SOURCE,
} from '../utility/queries';
import {
  IntegrationType,
  SourceType,
  UserCompanyLimitation,
} from '@sc/graphql';
import {
  Button,
  FullLoader,
  Input,
  transformIntegrationPath,
} from '@sc/shared-ui';
import { integrationOptions } from '../utility/integrations';
import { ConnectIntegration } from '../components/ConnectIntegration';
import { useForm } from '@tanstack/react-form';
import {
  DocumentIcon,
  FolderIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { notify } from '../utility/notify';
import { useAlerts } from '../providers/alerts';
import { DocumentBrowser } from '../modals/DocumentBrowser';
import { SourceDocumentCard } from '../components/SourceDocumentCard';
import { useUser } from '../providers/users';
import { track } from '../utility/analytics';

const NotionSource = ({ projectUuid }: { projectUuid: string }) => {
  const { data: integration } = useSuspenseQuery(GQL_QUERY_READ_INTEGRATION, {
    variables: {
      type: IntegrationType.notion,
    },
  });

  const { data: source } = useSuspenseQuery(GQL_QUERY_READ_SOURCE, {
    variables: {
      input: {
        type: SourceType.notion,
        projectUuid,
      },
    },
  });

  const { data: sourceDocuments } = useSuspenseQuery(
    GQL_QUERY_INDEX_SOURCE_DOCUMENTS,
    {
      variables: {
        dimensionsInput: undefined,
        input: {
          filter: {
            sourceUuid: source.readSource.uuid,
          },
          pagination: {
            perPage: 20,
            page: 1,
          },
        },
      },
    }
  );

  const { useApolloError } = useAlerts();

  const onError = useApolloError();

  const [updateSource] = useMutation(GQL_MUTATION_UPDATE_SOURCE, {
    onError,
    onCompleted: () => notify.success('Saved source name.'),
  });

  const form = useForm({
    defaultValues: {
      name: source.readSource.name ?? '',
    },
    onSubmit: ({ value }) => {
      if (value.name && value.name !== source.readSource.name) {
        void updateSource({
          variables: {
            input: {
              uuid: source.readSource.uuid,
              name: value.name,
            },
          },
        });
      }
    },
  });

  const [isAddingPages, setIsAddingPages] = useState(false);

  const { user } = useUser();

  if (!integration.readIntegration) {
    return (
      <ConnectIntegration
        integration={{
          ...integrationOptions.notion,
          oAuthUrl: integrationOptions.notion.oAuthUrl(projectUuid),
        }}
        onConnect={() =>
          track('Integration Connect Clicked', {
            integrationType: IntegrationType.notion,
            origin: 'project-sources',
          })
        }
      />
    );
  }

  return (
    <>
      <div className="overflow-y-auto flex flex-col h-full w-full">
        <div className="p-5 space-y-5">
          <form.Field
            name="name"
            children={({ state, handleChange }) => (
              <Input
                label="Source name (optional)"
                onBlur={() => void form.handleSubmit()}
                value={state.value}
                inputClassname="h-10!"
                onChange={(e) => handleChange(e.target.value)}
              />
            )}
          />
          <Button
            button={{
              text: 'Select pages',
              disabled: user.company.limitations.includes(
                UserCompanyLimitation.cannotCreateQueryMessage
              ),
              type: 'secondary',
              IconEnd: PlusIcon,
              onClick: () => setIsAddingPages(true),
            }}
          />
        </div>
        <div className="px-5 space-y-5 pb-5">
          {sourceDocuments.indexSourceDocuments.items.map((sd) => (
            <SourceDocumentCard
              sd={sd}
              sourceType={source.readSource.type}
              sourceUuid={source.readSource.uuid}
              FallbackIcon={
                sd.hostedIntegrationDocumentId?.includes('page')
                  ? DocumentIcon
                  : FolderIcon
              }
              key={sd.uuid}
              getPath={(sd) =>
                sd.hostedIntegrationPath &&
                transformIntegrationPath(sd.hostedIntegrationPath)
              }
            />
          ))}
        </div>
      </div>
      <DocumentBrowser
        open={isAddingPages}
        onClose={() => setIsAddingPages(false)}
        sourceUuid={source.readSource.uuid}
        projectUuid={projectUuid}
        type={IntegrationType.notion}
      />
    </>
  );
};

export const Notion = ({
  projectUuid,
}: {
  projectUuid: string;
}): ReactElement => (
  <Suspense fallback={<FullLoader className="size-5" />}>
    <NotionSource projectUuid={projectUuid} />
  </Suspense>
);
